import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  Collapse,
} from '@mui/material';
import {
  DataGrid,
  GridToolbar,
  GridActionsCellItem,
} from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Archive as ArchiveIcon,
  Visibility as ViewIcon,
  Tag as TagIcon,
  Cake as CakeIcon,
  FamilyRestroom as FamilyIcon,
  Public as PublicIcon,
  Work as WorkIcon,
  Language as LanguageIcon,
  AccessTime as TimeIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  LocationOn as LocationIcon,
  Add as AddIcon,
  FileDownload as ExportIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

const statusColors = {
  New: 'info',
  Active: 'success',
  Inactive: 'default',
  Prospect: 'warning',
  Lost: 'error',
  Converted: 'success',
  Qualified: 'primary',
  Nurturing: 'secondary',
};

function ContactList({
  contacts = [],
  loading = false,
  onContactSelect,
  onContactEdit,
  onContactDelete,
  onBulkAction,
  selectedContacts = [],
  onSelectionChange,
  onAddContact,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedContactForMenu, setSelectedContactForMenu] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedContact, setExpandedContact] = useState(null);

  // Filter contacts based on search and status
  const filteredContacts = useMemo(() => contacts.filter((contact) => {
    const matchesSearch = searchTerm === ''
        || Object.values(contact).some((val) => typeof val === 'string' && val.toLowerCase().includes(searchTerm.toLowerCase()))
        || (contact.tags || []).some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || contact.status === statusFilter;
    return matchesSearch && matchesStatus;
  }).map((contact) => ({
    ...contact,
    id: contact._id, // Add id field for DataGrid
  })), [contacts, searchTerm, statusFilter]);

  // Ensure every row has an id for DataGrid
  const rows = contacts.map((c, i) => ({
    ...c,
    id: c.id ?? c._id ?? i,
  }));
  console.log('DataGrid rows:', rows);

  // DataGrid columns configuration (expanded attributes)
  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
            {params.value ? params.value.charAt(0).toUpperCase() : '?'}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.value || 'No Name'}
            </Typography>
            {params.row.email && (
              <Typography variant="caption" color="text.secondary">
                {params.row.email}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <PhoneIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value || 'Unknown'}
          color={statusColors[params.value] || 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'company',
      headerName: 'Company',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <BusinessIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(params.value || []).slice(0, 2).map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          ))}
          {(params.value || []).length > 2 && (
            <Chip
              label={`+${(params.value || []).length - 2}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<MoreIcon />}
          label="More"
          onClick={(event) => {
            setSelectedContactForMenu(params.row);
            setAnchorEl(event.currentTarget);
          }}
        />,
      ],
    },
  ];

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedContactForMenu(null);
  };

  const handleBulkAction = (action) => {
    onBulkAction(action, selectedContacts);
    setSnackbar({
      open: true,
      message: `Bulk ${action} action performed`,
      severity: 'success',
    });
  };

  const handleDeleteContact = async () => {
    if (selectedContactForMenu) {
      await onContactDelete(selectedContactForMenu);
      setSnackbar({
        open: true,
        message: 'Contact deleted successfully',
        severity: 'success',
      });
    }
    handleMenuClose();
  };

  const handleContactExpand = (contactId) => {
    setExpandedContact(expandedContact === contactId ? null : contactId);
  };

  // Mobile contact card component
  function MobileContactCard({ contact }) {
    const isExpanded = expandedContact === contact._id;
    const isSelected = selectedContacts.includes(contact._id);

    return (
      <Card
        sx={{
          mb: 1,
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'divider',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => onContactSelect(contact)}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 48, height: 48 }}>
              {contact.name ? contact.name.charAt(0).toUpperCase() : '?'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {contact.name || 'No Name'}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {contact.email || 'No Email'}
              </Typography>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, mt: 0.5,
              }}
              >
                <Chip
                  label={contact.status || 'Unknown'}
                  color={statusColors[contact.status] || 'default'}
                  size="small"
                  variant="outlined"
                />
                {contact.company && (
                  <Typography variant="caption" color="text.secondary">
                    {contact.company}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleContactExpand(contact._id);
              }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={isExpanded}>
            <Box sx={{
              mt: 2, pt: 2, borderTop: 1, borderColor: 'divider',
            }}
            >
              <Stack spacing={1}>
                {contact.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{contact.phone}</Typography>
                  </Box>
                )}
                {contact.company && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="body2">{contact.company}</Typography>
                  </Box>
                )}
                {contact.tags && contact.tags.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {contact.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    ))}
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onContactEdit(contact);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      onContactDelete(contact);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Stack>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with search and filters */}
      <Paper elevation={1} sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
        <Stack
          direction={isSmallMobile ? 'column' : 'row'}
          spacing={2}
          alignItems={isSmallMobile ? 'stretch' : 'center'}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" fontWeight={600}>
            Contacts (
            {filteredContacts.length}
            )
          </Typography>
          {!isSmallMobile && <Box sx={{ flex: 1 }} />}
          <Tooltip title="Add Contact">
            <IconButton
              color="primary"
              onClick={onAddContact}
              size="small"
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
                alignSelf: isSmallMobile ? 'flex-end' : 'center',
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
          {selectedContacts.length > 0 && (
            <Alert severity="info" sx={{ py: 0 }}>
              {selectedContacts.length}
              {' '}
              contact(s) selected
            </Alert>
          )}
        </Stack>
        <Stack
          direction={isSmallMobile ? 'column' : 'row'}
          spacing={2}
          alignItems={isSmallMobile ? 'stretch' : 'center'}
        >
          <TextField
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: isSmallMobile ? '100%' : 400 }}
            size="small"
          />
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
            sx={{ minWidth: isSmallMobile ? '100%' : 120 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            {Object.keys(statusColors).map((status) => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </TextField>
          {selectedContacts.length > 0 && !isSmallMobile && (
            <Stack direction="row" spacing={1}>
              <Tooltip title="Archive Selected">
                <IconButton
                  size="small"
                  onClick={() => handleBulkAction('archive')}
                  color="warning"
                >
                  <ArchiveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Selected">
                <IconButton
                  size="small"
                  onClick={() => handleBulkAction('export')}
                  color="primary"
                >
                  <ExportIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Mobile List View */}
      {isMobile ? (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Typography>Loading contacts...</Typography>
            </Box>
          ) : filteredContacts.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Typography color="text.secondary">No contacts found</Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {filteredContacts.map((contact) => (
                <MobileContactCard key={contact._id} contact={contact} />
              ))}
            </Stack>
          )}
        </Box>
      ) : (
        /* Desktop DataGrid */
        <Paper elevation={1} sx={{ flex: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            getRowId={(row) => row._id || row.id}
            checkboxSelection
            onRowClick={(params) => {
              const contact = params.row;
              onContactSelect(contact);
            }}
            rowSelectionModel={selectedContacts}
            onRowSelectionModelChange={onSelectionChange}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 },
              },
              sorting: {
                sortModel: [{ field: 'createdAt', sort: 'desc' }],
              },
            }}
            slots={{
              toolbar: GridToolbar,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: false,
                csvOptions: { disableToolbarButton: false },
                printOptions: { disableToolbarButton: false },
              },
            }}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
                cursor: 'pointer',
              },
            }}
          />
        </Paper>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => onContactSelect(selectedContactForMenu)}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => onContactEdit(selectedContactForMenu)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Contact</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteContact}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete Contact</ListItemText>
        </MenuItem>
      </Menu>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ContactList;
